import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Moon, Sun, Shield, Mail, Copy, ExternalLink, Bell, Smartphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [name, setName] = useState(user?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  
  // Notification settings
  const [showInstallBanner, setShowInstallBanner] = useState(() => {
    return localStorage.getItem('hub_install_dismissed') !== 'true';
  });

  const handleToggleInstallBanner = (checked) => {
    setShowInstallBanner(checked);
    if (checked) {
      localStorage.removeItem('hub_install_dismissed');
      toast.success('Install banner will show on Hub page');
    } else {
      localStorage.setItem('hub_install_dismissed', 'true');
      toast.info('Install banner hidden');
    }
  };

  const handleSaveName = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.patch(`${API}/users/${user.user_id}`, { name }, { withCredentials: true });
      await refreshUser();
      toast.success('Name updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update name');
    } finally {
      setIsSaving(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    toast.success('Referral code copied!');
  };

  return (
    <DashboardLayout>
      <Header title="Settings" subtitle="Manage your account preferences" />
      
      <div className="p-6 space-y-6 max-w-3xl">
        {/* Profile Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-secondary uppercase">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
            <CardDescription>Your personal information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-lg">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <Badge className={`role-badge role-${user?.role_id} mt-1`}>
                  {user?.role_id?.replace('_', ' ')}
                </Badge>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                <div className="flex gap-2">
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="settings-name-input"
                  />
                  <Button onClick={handleSaveName} disabled={isSaving} data-testid="save-name-btn">
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  {user?.email}
                  <Badge variant="outline" className="text-xs">Verified</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-secondary uppercase">
              {theme === 'light' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              Appearance
            </CardTitle>
            <CardDescription>Customize how Leader Hub looks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">Switch between light and dark theme</p>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-secondary uppercase">
              <Bell className="w-5 h-5" />
              Notifications
            </CardTitle>
            <CardDescription>Control app notifications and banners</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Smartphone className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Add to Home Screen Banner</p>
                  <p className="text-sm text-muted-foreground">Show "+50 pts" install prompt on Hub page</p>
                </div>
              </div>
              <Switch
                checked={showInstallBanner}
                onCheckedChange={handleToggleInstallBanner}
                data-testid="install-banner-switch"
              />
            </div>
            
            <Separator />
            
            <div className="text-xs text-muted-foreground bg-secondary/50 p-3 rounded-lg">
              <p className="font-medium mb-1">💡 Tip:</p>
              <p>Install Leader Hub on your device for quick access and earn 50 bonus points!</p>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-secondary uppercase">
              <Shield className="w-5 h-5" />
              Security & Access
            </CardTitle>
            <CardDescription>Your account security settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Role</p>
                <p className="text-sm text-muted-foreground">Your assigned role in the system</p>
              </div>
              <Badge className={`role-badge role-${user?.role_id}`}>
                {user?.role_id?.replace('_', ' ')}
              </Badge>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Account Status</p>
                <p className="text-sm text-muted-foreground">Your account is currently active</p>
              </div>
              <Badge variant={user?.is_active ? 'default' : 'destructive'}>
                {user?.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium">Member Since</p>
                <p className="text-sm text-muted-foreground">When you joined Leader Hub</p>
              </div>
              <span className="text-sm">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Referral */}
        <Card>
          <CardHeader>
            <CardTitle className="font-secondary uppercase">Referral Program</CardTitle>
            <CardDescription>Share your code and earn 50 points for each referral</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <div className="flex-1 px-4 py-3 bg-secondary rounded-sm font-mono text-lg">
                {user?.referral_code}
              </div>
              <Button variant="outline" size="icon" onClick={copyReferralCode} data-testid="copy-referral-settings">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Share this code with friends. When they sign up, you both get 50 points!
            </p>
          </CardContent>
        </Card>

        {/* Connected Apps */}
        <Card>
          <CardHeader>
            <CardTitle className="font-secondary uppercase">Connected Apps</CardTitle>
            <CardDescription>Apps where you have used Leader Hub SSO</CardDescription>
          </CardHeader>
          <CardContent>
            {user?.connected_apps?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {user.connected_apps.map((app) => (
                  <Badge key={app} variant="secondary" className="px-3 py-1">
                    {app}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No apps connected yet. Use your Leader Hub account to sign in to connected apps.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
